import { UserStatus } from 'Util/UserUtils';
import { Conditions } from 'Util/QueryHelpers';
import { serialiseDateTimeUTC } from 'Util/AttributeUtils';

export interface IUserFilterState {
	role?: string,
	createdAfter?: Date,
	createdBefore?: Date,
	status?: string,
}

export interface IUserEntityFilterState {
	programCode?: string,
	programTitle?: string,
	lastAccessedAfter?: Date,
	lastAccessedBefore?: Date,
	status?: string,
}

// STATUS
const accountUnlockedFilters: Conditions[number] = [
	{
		path: 'LockoutEnd',
		comparison: 'LessThan',
		value: [serialiseDateTimeUTC(new Date())],
	},
	{
		path: 'LockoutEnd',
		comparison: 'Equal',
		value: [],
	},
];

export const getUserFilters = (filterValues: IUserFilterState): Conditions => {
	const newSearchParams: Conditions = [];

	// ROLE
	if (filterValues.role) {
		newSearchParams.push(
			[
				{
					path: 'discriminator',
					comparison: 'Equal',
					value: [filterValues.role],
				},
			],
		);
	}

	const filterEmailConfirmationBy = (isEmailConfirmed: boolean): Conditions[number] => (
		[
			{
				path: 'emailConfirmed',
				comparison: 'Equal',
				value: [isEmailConfirmed.toString()],
			},
		]
	);

	switch (filterValues.status) {
		case UserStatus.ACTIVE:
			newSearchParams.push(accountUnlockedFilters);
			newSearchParams.push(filterEmailConfirmationBy(true));
			break;
		case UserStatus.PENDING:
			newSearchParams.push(accountUnlockedFilters);
			newSearchParams.push(filterEmailConfirmationBy(false));
			break;
		case UserStatus.INACTIVE:
			newSearchParams.push(
				[
					{
						path: 'LockoutEnd',
						comparison: 'GreaterThanOrEqual',
						value: [serialiseDateTimeUTC(new Date())],
					},
				],
			);
			break;
	}

	// CREATED AFTER, CREATED BEFORE
	const filterCreatedBy = (
		direction: 'before' | 'afterOrEqual',
		value: Date,
	): Conditions[number] => ([
		{
			path: 'created',
			comparison: direction === 'before' ? 'LessThan' : 'GreaterThanOrEqual',
			value: [serialiseDateTimeUTC(value)],
		},
	]);

	if (filterValues.createdAfter) {
		newSearchParams.push(filterCreatedBy('afterOrEqual', filterValues.createdAfter));
	}

	if (filterValues.createdBefore) {
		newSearchParams.push(filterCreatedBy('before', filterValues.createdBefore));
	}

	return newSearchParams;
};

export const getUserEntityFilters = (filterValues: IUserEntityFilterState): Conditions => {
	const newSearchParams: Conditions = [];

	const filterEmailConfirmationBy = (isEmailConfirmed: boolean): Conditions[number] => (
		[
			{
				path: 'emailConfirmed',
				comparison: 'Equal',
				value: [isEmailConfirmed.toString()],
			},
		]
	);

	switch (filterValues.status) {
		case UserStatus.ACTIVE:
			newSearchParams.push(accountUnlockedFilters);
			newSearchParams.push(filterEmailConfirmationBy(true));
			break;
		case UserStatus.PENDING:
			newSearchParams.push(accountUnlockedFilters);
			newSearchParams.push(filterEmailConfirmationBy(false));
			break;
		case UserStatus.INACTIVE:
			newSearchParams.push(
				[
					{
						path: 'LockoutEnd',
						comparison: 'GreaterThanOrEqual',
						value: [serialiseDateTimeUTC(new Date())],
					},
				],
			);
			break;
	}

	// ACCESSED AFTER, ACCESSED BEFORE
	const filterLastAccessed = (
		direction: 'before' | 'afterOrEqual',
		value: Date,
	): Conditions[number] => ([
		{
			path: 'LastAccessed',
			comparison: direction === 'before' ? 'LessThan' : 'GreaterThanOrEqual',
			value: [serialiseDateTimeUTC(value)],
		},
	]);

	if (filterValues.lastAccessedAfter) {
		newSearchParams.push(filterLastAccessed('afterOrEqual', filterValues.lastAccessedAfter));
	}

	if (filterValues.lastAccessedBefore) {
		newSearchParams.push(filterLastAccessed('before', filterValues.lastAccessedBefore));
	}

	return newSearchParams;
};

export const getStudyPlanConditions = (filterValues: IUserEntityFilterState): Conditions => {
	const newSearchParams: Conditions = [];

	if (filterValues.programCode) {
		newSearchParams.push(
			[
				{
					path: 'programVersion.program.code',
					comparison: 'equal',
					value: [filterValues.programCode],
				},
			],
		);
	}

	if (filterValues.programTitle) {
		newSearchParams.push(
			[
				{
					path: 'programVersion.program.displayName',
					comparison: 'equal',
					value: [filterValues.programTitle],
				},
			],
		);
	}

	return newSearchParams;
};
